import { useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Download, Loader2 } from "lucide-react";
import { useWizard, type DocumentMetadata } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

const DOC_MIMES = [
  'image/jpeg', 'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
] as const;

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const EXT_TO_LABEL: Record<string, string> = {
  '.jpg': 'JPEG · PNG', '.jpeg': 'JPEG · PNG', '.png': 'JPEG · PNG',
  '.pdf': 'PDF',
  '.doc': 'Word', '.docx': 'Word',
  '.xls': 'Excel', '.xlsx': 'Excel',
  '.csv': 'CSV',
};

function buildFormatHint(accept: string): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const ext of accept.split(',').map(s => s.trim().toLowerCase())) {
    const label = EXT_TO_LABEL[ext];
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels.length > 0 ? `Accepted: ${labels.join(' · ')} — max 10 MB` : 'Max 10 MB';
}

interface DocumentUploadSectionProps {
  fieldIdentifier: string;
  documents: DocumentMetadata[];
  onDocumentsChange: (documents: DocumentMetadata[]) => void;
  currentTab?: "general" | "environment" | "social" | "governance"; // Current wizard tab for scoped invalidation
  label?: string;
  description?: string;
  accept?: string;
  maxFiles?: number;
  borderColor?: string;
  className?: string;
  itemClassName?: string;
  actionButtonClassName?: string;
  hideHeader?: boolean;
  emptyStateContent?: React.ReactNode;
  emptyStateButtonClassName?: string;
  documentsListClassName?: string;
}

export function DocumentUploadSection({
  fieldIdentifier,
  documents,
  onDocumentsChange,
  currentTab,
  label,
  description,
  accept = ".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv",
  maxFiles = 10,
  borderColor = "border-primary",
  className,
  itemClassName,
  actionButtonClassName,
  hideHeader = false,
  emptyStateContent,
  emptyStateButtonClassName,
  documentsListClassName,
}: DocumentUploadSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const invalidationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { uploadDocument, deleteDocument, getDocumentDownloadUrl, isLocked, isReadOnly, assessmentId } = useWizard();
  // Read-only viewers (portfolio managers, admins on another company) can view/download but not upload or delete.
  const locked = isLocked || isReadOnly;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const displayLabel = label ?? "Supporting Documents";
  const displayDescription = description ?? "Upload supporting documents for this section.";

  // Debounced invalidation - batch multiple uploads
  const scheduleInvalidation = useCallback(() => {
    if (invalidationTimerRef.current) {
      clearTimeout(invalidationTimerRef.current);
    }

    invalidationTimerRef.current = setTimeout(() => {
      if (assessmentId && currentTab) {
        // Only invalidate the query for the current tab
        queryClient.invalidateQueries({ queryKey: [currentTab, assessmentId] });
      }
      invalidationTimerRef.current = null;
    }, 500); // 500ms debounce
  }, [assessmentId, currentTab, queryClient]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // First pass: validate all files; collect valid ones and report invalid ones
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!DOC_MIMES.includes(file.type as any)) {
        toast({
          title: "Unsupported file type",
          description: `"${file.name}" — Accepted formats: JPEG, PNG, PDF, Word, Excel, CSV.`,
          variant: "destructive",
        });
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast({
          title: "File too large",
          description: `"${file.name}" exceeds the 10 MB limit.`,
          variant: "destructive",
        });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (documents.length + validFiles.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `File limit for this field is ${maxFiles}.`,
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    const newDocuments: DocumentMetadata[] = [];

    for (const file of validFiles) {
      const result = await uploadDocument(file, fieldIdentifier, currentTab);
      if (result.ok) {
        newDocuments.push(result.data);
        toast({ title: "File uploaded", description: file.name });
      } else {
        toast({ title: "Upload failed", description: result.error, variant: "destructive" });
      }
    }

    if (newDocuments.length > 0) {
      onDocumentsChange([...documents, ...newDocuments]);
      scheduleInvalidation();
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (doc: DocumentMetadata) => {
    if (locked) {
      toast({
        title: "Assessment is locked",
        description: "Files cannot be deleted after locking.",
        variant: "destructive",
      });
      return;
    }

    if (!confirm(`Delete "${doc.name}"?`)) return;

    setDeletingId(doc.id);
    const success = await deleteDocument(doc.id);
    setDeletingId(null);

    if (success) {
      onDocumentsChange(documents.filter(d => d.id !== doc.id));
      toast({ title: "File deleted", description: doc.name });
    } else {
      toast({ title: "Could not delete file", description: doc.name, variant: "destructive" });
    }
  };

  const handleDownload = (doc: DocumentMetadata) => {
    const url = getDocumentDownloadUrl(doc.id);
    window.open(url, '_blank');
  };

  const uploadDisabled = uploading || documents.length >= maxFiles || locked;

  const uploadAction = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => fileInputRef.current?.click()}
      disabled={uploadDisabled}
      className={actionButtonClassName ?? "gap-1"}
    >
      {uploading ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          Uploading…
        </>
      ) : (
        <>
          <Upload className="h-3 w-3" />
          Upload files
        </>
      )}
    </Button>
  );

  return (
    <div className={className ?? `rounded-lg border bg-muted/30 p-4 ${borderColor}`}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {!hideHeader && (
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h4 className="text-sm font-medium text-foreground">{displayLabel}</h4>
            <p className="mt-1 text-xs text-muted-foreground">{displayDescription}</p>
            <p className="mt-1 text-xs text-muted-foreground/70">{buildFormatHint(accept)}</p>
          </div>
          {uploadAction}
        </div>
      )}

      {hideHeader && documents.length > 0 && (
        <div className="mb-4 flex justify-end">
          {uploadAction}
        </div>
      )}

      {hideHeader && (
        <p className="mb-2 text-xs text-muted-foreground/70">{buildFormatHint(accept)}</p>
      )}

      {documents.length > 0 && (
        <div className={cn("space-y-2", documentsListClassName)}>
          {documents.map((doc) => (
            <div
              key={doc.id}
              className={itemClassName ?? "flex items-center justify-between rounded border bg-white p-2"}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate" title={doc.name}>
                  {doc.name}
                </span>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {new Date(doc.uploadedAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(doc)}
                  className="h-7 w-7 p-0"
                  title="Download"
                >
                  <Download className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(doc)}
                  disabled={deletingId === doc.id || locked}
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  title={isReadOnly ? "Read-only access" : isLocked ? "Assessment is locked" : "Delete"}
                >
                  {deletingId === doc.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {documents.length === 0 && (
        emptyStateContent ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadDisabled}
            className={cn("w-full text-left", emptyStateButtonClassName)}
          >
            {emptyStateContent}
          </button>
        ) : (
          <p className="text-xs text-muted-foreground italic text-center py-2">
            No documents uploaded yet.
          </p>
        )
      )}

      {documents.length >= maxFiles && (
        <p className="text-xs text-amber-600 mt-2">
          {`Maximum ${maxFiles} files for this field.`}
        </p>
      )}
    </div>
  );
}
